/**
 * Timezone arithmetic without a library.
 *
 * Everything the application stores is an instant (a timestamptz in
 * Postgres, an epoch-millisecond number or ISO string here). Everything a
 * person reads or types is wall-clock time in a NAMED zone — the
 * location's, the provider's, the customer's. These helpers convert
 * between the two using the runtime's own tz database through Intl, which
 * Cloudflare Workers, Node and browsers all ship, so there is no data to
 * keep current and nothing to bundle.
 *
 * DAYLIGHT SAVING
 *
 * A wall-clock time can map to zero instants (the hour that is skipped
 * when clocks go forward) or two (the hour that repeats when they go
 * back). zonedToUtc makes both cases explicit:
 *
 *   - skipped   -> the time is moved FORWARD by the size of the gap, the
 *                  way a clock on the wall would show it, and `exists` is
 *                  false so a caller can refuse it if it wants to.
 *   - repeated  -> the FIRST occurrence (the earlier instant), and
 *                  `ambiguous` is true.
 *
 * Availability rules are generated instant by instant from wall-clock
 * rules, so on a day that is 23 or 25 hours long the schedule is still
 * "09:00 to 17:00 local", which is what the business wrote down.
 */

export interface WallClock {
  year: number;
  month: number; // 1-12
  day: number; // 1-31
  hour: number; // 0-23
  minute: number; // 0-59
  second?: number;
}

export interface ZonedParts extends Required<WallClock> {
  /** 0 = Sunday ... 6 = Saturday, in the zone. */
  weekday: number;
  /** Minutes east of UTC in effect at this instant, e.g. -300 for EST. */
  offsetMinutes: number;
  /** YYYY-MM-DD in the zone. */
  date: string;
  /** HH:MM in the zone. */
  time: string;
}

const MINUTE = 60_000;
const HOUR = 3_600_000;

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      weekday: 'short',
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

const WEEKDAYS: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
};

/** True when the runtime knows the zone. Used to validate user input. */
export function isValidTimeZone(timeZone: string): boolean {
  if (typeof timeZone !== 'string' || timeZone.length === 0 || timeZone.length > 64) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0);
    return true;
  } catch {
    return false;
  }
}

/** Wall-clock parts of an instant in a zone. */
export function toZoned(timeZone: string, instant: number | Date | string): ZonedParts {
  const ms = toMillis(instant);
  const parts = formatter(timeZone).formatToParts(new Date(ms));
  let year = 0, month = 0, day = 0, hour = 0, minute = 0, second = 0, weekday = 0;
  for (const p of parts) {
    switch (p.type) {
      case 'year': year = Number(p.value); break;
      case 'month': month = Number(p.value); break;
      case 'day': day = Number(p.value); break;
      case 'hour': hour = Number(p.value) % 24; break;
      case 'minute': minute = Number(p.value); break;
      case 'second': second = Number(p.value); break;
      case 'weekday': weekday = WEEKDAYS[p.value] ?? 0; break;
    }
  }
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  const offsetMinutes = Math.round((asUtc - Math.floor(ms / 1000) * 1000) / MINUTE);
  return {
    year, month, day, hour, minute, second, weekday, offsetMinutes,
    date: `${pad(year, 4)}-${pad(month)}-${pad(day)}`,
    time: `${pad(hour)}:${pad(minute)}`,
  };
}

/** Minutes east of UTC that the zone is using at the instant. */
export function offsetAt(timeZone: string, instant: number | Date | string): number {
  return toZoned(timeZone, instant).offsetMinutes;
}

export interface ZonedToUtcResult {
  /** Epoch milliseconds. */
  ms: number;
  /** False when the wall-clock time was skipped by a DST transition. */
  exists: boolean;
  /** True when the wall-clock time occurs twice; `ms` is the first. */
  ambiguous: boolean;
}

/** The instant at which a zone's clocks read the given wall-clock time. */
export function zonedToUtc(timeZone: string, wall: WallClock): ZonedToUtcResult {
  const naive = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, wall.second ?? 0);
  // Every offset the zone could plausibly be using around this time.
  const candidates = new Set<number>([
    offsetAt(timeZone, naive - 12 * HOUR),
    offsetAt(timeZone, naive),
    offsetAt(timeZone, naive + 12 * HOUR),
  ]);
  const valid: number[] = [];
  for (const offset of candidates) {
    const ms = naive - offset * MINUTE;
    if (offsetAt(timeZone, ms) === offset) valid.push(ms);
  }
  if (valid.length === 0) {
    // Skipped: use the offset in force BEFORE the transition, which moves
    // the clock forward across the gap (02:30 -> 03:30).
    const before = Math.min(...candidates);
    return { ms: naive - before * MINUTE, exists: false, ambiguous: false };
  }
  valid.sort((a, b) => a - b);
  return { ms: valid[0]!, exists: true, ambiguous: valid.length > 1 };
}

/** Parse 'YYYY-MM-DD' into calendar numbers, or null. */
export function parseDate(date: string): { year: number; month: number; day: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const check = new Date(Date.UTC(year, month - 1, day));
  if (check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
  return { year, month, day };
}

/** Parse 'HH:MM' or 'HH:MM:SS' into minutes since midnight, or null. */
export function parseTime(time: string): number | null {
  const m = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(time);
  if (!m) return null;
  const h = Number(m[1]), min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min > 0)) return null;
  return h * 60 + min;
}

/** The instant a local calendar day starts in a zone. */
export function startOfDay(timeZone: string, date: string): number {
  const d = parseDate(date);
  if (!d) throw new Error(`Invalid date: ${date}`);
  return zonedToUtc(timeZone, { ...d, hour: 0, minute: 0 }).ms;
}

/** [start, end) of a local calendar day in a zone. Handles 23/25-hour days. */
export function dayRange(timeZone: string, date: string): { start: number; end: number } {
  const start = startOfDay(timeZone, date);
  const end = startOfDay(timeZone, addDays(date, 1));
  return { start, end };
}

/** 'YYYY-MM-DD' plus n days, as a calendar operation (no zone involved). */
export function addDays(date: string, days: number): string {
  const d = parseDate(date);
  if (!d) throw new Error(`Invalid date: ${date}`);
  const t = Date.UTC(d.year, d.month - 1, d.day + days);
  const x = new Date(t);
  return `${pad(x.getUTCFullYear(), 4)}-${pad(x.getUTCMonth() + 1)}-${pad(x.getUTCDate())}`;
}

/** The instant a local date and 'HH:MM' represent in a zone. */
export function localToUtc(timeZone: string, date: string, time: string): ZonedToUtcResult {
  const d = parseDate(date);
  const minutes = parseTime(time);
  if (!d || minutes === null) throw new Error(`Invalid local time: ${date} ${time}`);
  return zonedToUtc(timeZone, { ...d, hour: Math.floor(minutes / 60), minute: minutes % 60 });
}

/** ISO 8601 UTC string with millisecond precision. */
export function toIso(instant: number | Date | string): string {
  return new Date(toMillis(instant)).toISOString();
}

export function toMillis(instant: number | Date | string): number {
  if (typeof instant === 'number') return instant;
  if (instant instanceof Date) return instant.getTime();
  const ms = Date.parse(instant);
  if (Number.isNaN(ms)) throw new Error(`Invalid instant: ${instant}`);
  return ms;
}

/** Human-readable local time, e.g. "Tue, Mar 10, 2026, 2:30 PM EDT". */
export function formatInZone(
  timeZone: string,
  instant: number | Date | string,
  options: Intl.DateTimeFormatOptions = {},
  locale = 'en-US',
): string {
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    ...options,
  }).format(new Date(toMillis(instant)));
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}
